import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { PlanRow } from 'modules/campaign/row';
import { EntityKey, gridAtom } from './grid';
import { planAtom } from './plan';

export const selectionEnabledAtom = atom<boolean>(false);

export const selectedRowsAtom = atom<Set<EntityKey>>(new Set<EntityKey>());

function areAllDescendantsSelected(
  groupKey: EntityKey,
  entities: Record<EntityKey, { type: 'GROUP' | 'ROW'; id: number }>,
  childrenMap: Record<EntityKey, EntityKey[]>,
  selectedRows: Set<EntityKey>,
): boolean {
  const children = childrenMap[groupKey] || [];
  if (children.length === 0) return true;

  for (const childKey of children) {
    const ref = entities[childKey];
    if (!ref) continue;

    if (ref.type === 'ROW') {
      if (!selectedRows.has(childKey)) return false;
    } else {
      if (!areAllDescendantsSelected(childKey, entities, childrenMap, selectedRows)) {
        return false;
      }
    }
  }
  return true;
}

function collectDescendantRows(
  groupKey: EntityKey,
  entities: Record<EntityKey, { type: 'GROUP' | 'ROW'; id: number }>,
  childrenMap: Record<EntityKey, EntityKey[]>,
  result: Set<EntityKey>,
): void {
  const children = childrenMap[groupKey] || [];
  for (const childKey of children) {
    const ref = entities[childKey];
    if (!ref) continue;

    if (ref.type === 'ROW') {
      result.add(childKey);
    } else {
      collectDescendantRows(childKey, entities, childrenMap, result);
    }
  }
}

export const gridSelectionAtom = atom(
  (get): ReadonlySet<EntityKey> => {
    const selectedRows = get(selectedRowsAtom);
    const { entities, childrenMap } = get(gridAtom);
    const result = new Set(selectedRows);

    for (const key of Object.keys(entities)) {
      const ref = entities[key];
      if (ref.type === 'GROUP') {
        if (areAllDescendantsSelected(key, entities, childrenMap, selectedRows)) {
          result.add(key);
        }
      }
    }

    return result;
  },
  (get, set, newGridSelection: ReadonlySet<EntityKey>) => {
    const { entities, childrenMap } = get(gridAtom);
    const oldGridSelection = get(gridSelectionAtom);
    const newRawSelection = new Set(get(selectedRowsAtom));

    const allKeys = new Set([...oldGridSelection, ...newGridSelection]);
    for (const key of allKeys) {
      const wasSelected = oldGridSelection.has(key);
      const isSelected = newGridSelection.has(key);

      if (wasSelected === isSelected) continue;

      const ref = entities[key];
      if (!ref) continue;

      if (ref.type === 'ROW') {
        if (isSelected) {
          newRawSelection.add(key);
        } else {
          newRawSelection.delete(key);
        }
      } else {
        const descendantRows = new Set<EntityKey>();
        collectDescendantRows(key, entities, childrenMap, descendantRows);

        for (const rowKey of descendantRows) {
          if (isSelected) {
            newRawSelection.add(rowKey);
          } else {
            newRawSelection.delete(rowKey);
          }
        }
      }
    }

    set(selectedRowsAtom, newRawSelection);
  },
);

const planRowSelectionAtom = atom<PlanRow[]>((get) => {
  const selectedKeys = get(selectedRowsAtom);
  const { entities } = get(gridAtom);
  const { rows } = get(planAtom);

  const result: PlanRow[] = [];
  for (const key of selectedKeys) {
    const ref = entities[key];
    if (ref?.type === 'ROW') {
      result.push(rows[ref.id]);
    }
  }
  return result;
});

export function useGridSelection() {
  return useAtom(gridSelectionAtom);
}

export function useToolbarSelection(): [selected: PlanRow[], celarSelection: () => void] {
  const selected = useAtomValue(planRowSelectionAtom);

  const setSelectedRows = useSetAtom(selectedRowsAtom);
  const celarSelection = () => setSelectedRows(new Set());
  return [selected, celarSelection];
}
